import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { DATASET } from '@utils/constants';
import { useEventMetricsQueryManager, useEventMetricsQueryStatus } from '../../queryManager';
import { EVENT_METRICS_LIST } from '@src/hooks/event/useEventConfigs';

/**
 * Create a query configuration for event metrics data
 * @param eventName - The event name to query (sent as signal_name_param)
 * @param interval - Time interval for the query
 * @param granularity - Granularity of the query
 * @param filter - Base filters (event filter NOT included)
 * @param options - Additional query options
 */
export const createEventMetricsQuery = (
  eventName: string,
  interval,
  granularity = 'ALL',
  filter = [] as any[][],
  options = {},
) => {
  const query = {
    type: 'timeseries',
    dataset: DATASET,
    granularity: granularity,
    options: {
      withTotals: true,
      ...(options || {}),
    },
    interval,
    filter,
    metrics: EVENT_METRICS_LIST,
    parameters: {
      signal_name_param: eventName,
    },
    context: window.conviva.context,
  };

  return {
    queries: [query],
  };
};

/**
 * Hook for querying event metrics data with concurrency management
 */
function useEventMetricsQuery({ eventName, queryKey, queryFn, options }) {
  const queryManager = useEventMetricsQueryManager(eventName);
  const { queued } = useEventMetricsQueryStatus(eventName);

  const queryRes = useQuery(queryKey, () => queryManager.exec(queryFn), {
    ...options,
  });

  const res = useMemo(
    () => ({
      ...queryRes,
      queued,
    }),
    [queryRes, queued],
  );

  // cancel any queued query on unmount
  useEffect(() => {
    return () => {
      queryManager.abort();
    };
  }, []);

  return res;
}

export default useEventMetricsQuery;
